import React from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  useWindowDimensions,
  Linking,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useAuth } from '../../hooks/useAuth';
import { useRouter } from 'expo-router';
import { useLanguage } from '../../contexts/LanguageContext';

const GRID_PADDING = 24;
const GRID_GAP = 16;
const ICON_SIZE_RATIO = 0.32;
const FONT_SIZE_RATIO = 0.12;
const MAX_BUTTON_SIZE = 160;

export default function HomeScreen() {
  const { appUser, firebaseUser } = useAuth();
  const router = useRouter();
  const { t } = useLanguage();
  const { width } = useWindowDimensions();

  const handleContactPress = () => {
    const email = 'l.derwich@bieszczady.gopr.pl';
    const subject = encodeURIComponent(t('home.contactEmailSubject'));
    const body = encodeURIComponent(t('home.contactEmailBody'));
    Linking.openURL(`mailto:${email}?subject=${subject}&body=${body}`);
  };

  const role = appUser?.role ?? 'guest';
  const canView = role !== 'guest';
  const canManage = role === 'admin' || role === 'manager';
  const isAdmin = role === 'admin';

  const navigationButtons = [
    {
      key: 'usage',
      title: t('home.navigation.usage'),
      icon: 'clipboard-outline' as const,
      route: '/(app)/usage',
      color: '#2196F3',
      show: canView,
    },
    {
      key: 'documents',
      title: t('home.navigation.documents'),
      icon: 'document-text-outline' as const,
      route: '/(app)/documents',
      color: '#9C27B0',
      show: canView,
    },
    {
      key: 'inventory',
      title: t('home.navigation.inventory'),
      icon: 'cube-outline' as const,
      route: '/(app)/categories',
      color: '#4CAF50',
      show: canManage,
    },
    {
      key: 'stores',
      title: t('home.navigation.stores'),
      icon: 'business-outline' as const,
      route: '/(app)/stores',
      color: '#FF9800',
      show: canView,
    },
    {
      key: 'reports',
      title: t('home.navigation.reports'),
      icon: 'bar-chart-outline' as const,
      route: '/(app)/reports',
      color: '#00897B',
      show: canManage,
    },
    {
      key: 'admin',
      title: t('home.navigation.admin'),
      icon: 'shield-outline' as const,
      route: '/(app)/admin',
      color: '#F44336',
      show: isAdmin,
    },
    {
      key: 'auditLogs',
      title: t('home.navigation.auditLogs'),
      icon: 'list-outline' as const,
      route: '/(app)/audit-logs',
      color: '#607D8B',
      show: isAdmin,
    },
  ].filter(btn => btn.show);

  const columns = width >= 1024 ? 5 : width >= 768 ? 4 : 2;
  const rawButtonSize =
    (width - GRID_PADDING * 2 - GRID_GAP * (columns - 1)) / columns;
  const buttonSize = Math.min(rawButtonSize, MAX_BUTTON_SIZE);

  const roleColor: Record<string, string> = {
    admin: '#DC2626',
    manager: '#D97706',
    user: '#2563EB',
    guest: '#6B7280',
  };

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      <Text style={styles.welcome}>{t('home.welcome')}</Text>
      <Text style={styles.name}>
        {appUser?.firstname ?? firebaseUser?.displayName ?? 'User'}
      </Text>
      <View style={[styles.roleBadge, { backgroundColor: roleColor[role] }]}>
        <Text style={styles.roleText}>{role.toUpperCase()}</Text>
      </View>
      <Text style={styles.email}>{appUser?.email ?? firebaseUser?.email}</Text>

      {role === 'guest' && (
        <View style={styles.guestNoticeCard}>
          <Ionicons name="lock-closed-outline" size={32} color="#6B7280" style={styles.guestNoticeIcon} />
          <Text style={styles.guestNoticeText}>{t('home.guestNotice')}</Text>
          <TouchableOpacity style={styles.contactButton} onPress={handleContactPress} activeOpacity={0.85}>
            <Ionicons name="mail-outline" size={18} color="#FFFFFF" />
            <Text style={styles.contactButtonText}>{t('home.contactButton')}</Text>
          </TouchableOpacity>
        </View>
      )}

      {navigationButtons.length > 0 && (
        <View style={styles.navigationGrid}>
          {navigationButtons.map(btn => (
            <TouchableOpacity
              key={btn.key}
              style={[
                styles.navigationButton,
                { backgroundColor: btn.color, width: buttonSize, height: buttonSize },
              ]}
              onPress={() => router.push(btn.route as any)}
              activeOpacity={0.85}
            >
              <View style={styles.buttonContent}>
                <Ionicons name={btn.icon} size={buttonSize * ICON_SIZE_RATIO} color="#FFFFFF" />
                <Text style={[styles.buttonText, { fontSize: buttonSize * FONT_SIZE_RATIO }]}>
                  {btn.title}
                </Text>
              </View>
            </TouchableOpacity>
          ))}
        </View>
      )}

      {/*<View style={styles.langCard}>
        <Text style={styles.cardTitle}>{t('home.language')}</Text>
        <View style={styles.langRow}>
          {(['pl', 'en'] as Language[]).map(lang => (
            <TouchableOpacity
              key={lang}
              style={[styles.langBtn, language === lang && styles.langBtnActive]}
              onPress={() => handleLanguageChange(lang)}
            >
              <Text style={[styles.langBtnText, language === lang && styles.langBtnTextActive]}>
                {lang.toUpperCase()}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>*/}
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#F8FAFC' },
  content: { padding: GRID_PADDING, alignItems: 'center', maxWidth: 1200, width: '100%', alignSelf: 'center' },
  welcome: { fontSize: 20, color: '#64748B', marginTop: 16 },
  name: { fontSize: 28, fontWeight: 'bold', color: '#1E293B', marginTop: 4 },
  roleBadge: {
    paddingHorizontal: 16, paddingVertical: 6, borderRadius: 20, marginTop: 12,
  },
  roleText: { color: '#FFF', fontWeight: '700', fontSize: 13 },
  email: { fontSize: 14, color: '#64748B', marginTop: 8 },
  navigationGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: GRID_GAP,
    marginTop: 32,
    width: '100%',
  },
  navigationButton: {
    borderRadius: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 4.65,
    elevation: 8,
  },
  buttonContent: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 12,
  },
  buttonText: {
    color: '#FFFFFF',
    fontWeight: '700',
    textAlign: 'center',
    lineHeight: 20,
  },
  langCard: {
    backgroundColor: '#FFF', borderRadius: 12, padding: 20,
    width: '100%', marginTop: 32, shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.06,
    shadowRadius: 8, elevation: 2,
  },
  cardTitle: { fontSize: 16, fontWeight: '700', color: '#1E293B', marginBottom: 12 },
  langRow: { flexDirection: 'row', gap: 12 },
  langBtn: {
    paddingHorizontal: 24, paddingVertical: 10, borderRadius: 8,
    borderWidth: 1, borderColor: '#E2E8F0', backgroundColor: '#F8FAFC',
  },
  langBtnActive: { backgroundColor: '#2563EB', borderColor: '#2563EB' },
  langBtnText: { fontSize: 14, fontWeight: '700', color: '#475569' },
  langBtnTextActive: { color: '#FFF' },
  guestNoticeCard: {
    backgroundColor: '#FFF',
    borderRadius: 16,
    padding: 24,
    width: '100%',
    marginTop: 32,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 8,
    elevation: 3,
    borderWidth: 1,
    borderColor: '#E2E8F0',
  },
  guestNoticeIcon: { marginBottom: 12 },
  guestNoticeText: {
    fontSize: 15,
    color: '#374151',
    textAlign: 'center',
    lineHeight: 22,
    marginBottom: 20,
  },
  contactButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#2563EB',
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 10,
    gap: 8,
  },
  contactButtonText: {
    color: '#FFF',
    fontSize: 15,
    fontWeight: '600',
  },
  signOutBtn: {
    backgroundColor: '#EF4444', paddingHorizontal: 32, paddingVertical: 14,
    borderRadius: 8, marginTop: 32, minWidth: 200, alignItems: 'center',
    marginBottom: 8,
  },
  signOutText: { color: '#FFF', fontSize: 16, fontWeight: '600' },
});
